import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { auth, sites as sitesSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';
import { setIsMapDataUpdateNeeded } from '@slices/map';

import {
	createSite,
	editSite,
	getSiteId,
	getSitesDB,
	removeSites,
} from '@api/manageSite';

import { isNaN } from '@utils/common';
import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IEditSiteDTO, IManageSiteValuesDTO, ISitesDTO } from '@interfaces/cells/dto/cellsDTO';
import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { EMenuActionsTypes, ESnackbarTypes } from '@enums/common';

import {
	CREATE_SITE,
	EDIT_SITE,
	GENERATE_SITE_ID,
	REMOVE_SITE,
	REMOVE_SITES,
	SITE_PROFILE,
	SITE_TYPES,
	SITES_DB,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseManageSiteParams {
	closeSiteModal: () => void;
	closeDeleteConfirm?: () => void;
}

export type TGenerateSiteId = (value: { setSiteId: (id: number) => void }) => void;

export const useManageSites = (params: IUseManageSiteParams) => {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		closeSiteModal,
		closeDeleteConfirm,
	} = params || {};

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		vendor,
		siteType,
		technology,
		installedOnAsset,
		siteId,
		searchValue,
	} = useAppSelector(sitesSelector);

	const { activeOrganisation: organizationId } = useAppSelector(auth);

	const {
		data,
		isLoading: isLoadingSitesDB,
		isFetching: isFetchingSitesDB,
	} = useQuery({
		queryKey: [
			SITES_DB,
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			vendor,
			siteType,
			technology,
			installedOnAsset,
			siteId,
			searchValue,
			organizationId,
		],
		queryFn: () => getSitesDB({
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			vendor,
			siteType,
			technology,
			installedOnAsset,
			searchValue,
			siteId: (isNaN(+siteId) || !siteId.length) ? null : +siteId,
		}),
		enabled: [organizationId],
		refetchOnWindowFocus: false,
	});

	const {
		mutate: handleDeleteSite,
		isLoading: isLoadingDeleteSite,
	} = useMutation<string>({
		mutationKey: [REMOVE_SITE],
		mutationFn: (id: string) => removeSites({
			ids: [id],
			isAll: false,
			filter: null,
		}),
		onSuccess: () => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: responseMessages.removedSite,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([SITES_DB]);
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDeleteSites,
		isLoading: isLoadingDeleteSites,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [
			REMOVE_SITES,
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			vendor,
			siteType,
			technology,
			installedOnAsset,
			searchValue,
			siteId,
			organizationId,
		],
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const filter: ISitesDTO = {
				page,
				pageSize,
				sortDirection,
				sortFieldName,
				vendor,
				siteType,
				technology,
				installedOnAsset,
				searchValue,
				siteId: (isNaN(+siteId) || !siteId.length) ? null : +siteId,
				organizationId,
			};

			return removeSites({
				filter: (actionType === EMenuActionsTypes.allFiltered) && filter
					? filter
					: null,
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: () => {
			if (closeDeleteConfirm) closeDeleteConfirm();
		},
		onError: (err: IResponseError) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: createSiteHandler,
		isLoading: isLoadingCreateSite,
	} = useMutation<IManageSiteValuesDTO>({
		mutationKey: [CREATE_SITE],
		mutationFn: createSite,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.createdSite,
				type: ESnackbarTypes.success,
			}));

			closeSiteModal();

			return [
				queryClient.invalidateQueries([SITES_DB]),
				queryClient.invalidateQueries([SITE_TYPES]),
			];
		},
	});

	const {
		mutate: editSiteHandler,
		isLoading: isLoadingEditSite,
	} = useMutation<IEditSiteDTO>({
		mutationKey: [EDIT_SITE],
		mutationFn: editSite,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updatedSite,
				type: ESnackbarTypes.success,
			}));
			dispatch(setIsMapDataUpdateNeeded(true));

			closeSiteModal();

			return [
				queryClient.invalidateQueries([SITES_DB]),
				queryClient.invalidateQueries([SITE_PROFILE]),
			];
		},
	});

	const {
		mutate: generateSiteId,
		isLoading: isLoadingGenerateSiteId,
	} = useMutation({
		mutationKey: [GENERATE_SITE_ID],
		mutationFn: ({ setSiteId }: { setSiteId: (id: number) => void }) => getSiteId().then(data => {
			if (data.id >= 0) setSiteId(data.id);
		}),
	});

	return {
		isLoadingForm: isLoadingCreateSite || isLoadingGenerateSiteId,
		generateSiteId: generateSiteId as unknown as TGenerateSiteId,
		createSiteHandler,
		isLoadingSites: isLoadingSitesDB || isFetchingSitesDB || isLoadingDeleteSite
			|| isLoadingEditSite,
		sitesDB: data?.items || [],
		countOfRecords: data?.count || 0,
		handleDeleteSite,
		handleDeleteSites,
		editSiteHandler,
		isLoadingDeleteSites,
	};
};
